import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, ScanLine, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { http, apiError, money } from "@/lib/shop";
import { PartRow } from "./PartRow";
import { PartForm } from "./PartForm";
import { StockDialog } from "./StockDialog";
import { ScannerDialog } from "./ScannerDialog";
import { MovementHistory } from "./MovementHistory";

export const PartsInventory = ({ owner }) => {
  const [parts, setParts] = useState(null);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [low, setLow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [stock, setStock] = useState(null);
  const [history, setHistory] = useState(null);
  const [scan, setScan] = useState(false);
  const [highlight, setHighlight] = useState(null);

  const load = useCallback(() => {
    const params = { low };
    if (q.trim()) params.q = q.trim();
    if (cat !== "all") params.category = cat;
    http.get("/parts", { params }).then((r) => setParts(r.data)).catch((e) => toast.error(apiError(e)));
    http.get("/parts/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, [q, cat, low]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const save = async (data) => {
    try {
      if (editing?.id) await http.put(`/parts/${editing.id}`, data);
      else await http.post("/parts", data);
      toast.success("Part saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.name}? Its stock history is kept.`)) return;
    try { await http.delete(`/parts/${p.id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const onScanned = useCallback(async (code) => {
    try {
      const { data } = await http.get(`/parts/by-code/${encodeURIComponent(code)}`);
      setQ(""); setCat("all"); setLow(false);
      setHighlight(data.id);
      setStock({ part: data, type: "out" });
      toast.success(`Found: ${data.name}`);
    } catch {
      setQ(code);
      toast.error(owner ? "No part with that code yet — add it as a new part." : "No part with that code.");
      if (owner) setEditing({ barcode: code });
    }
  }, [owner]);

  return (
    <div className="mt-6" data-testid="parts-inventory">
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <Stat label="Parts" value={summary.total_parts} testid="stat-parts" />
          <Stat label="Units in stock" value={summary.total_units} testid="stat-units" />
          <Stat label="Low stock" value={summary.low_stock} warn={summary.low_stock > 0} testid="stat-low" onClick={() => setLow(true)} />
          {owner ? <Stat label="Stock value (cost)" value={money(summary.stock_cost_value)} sub={`Parts profit from repairs: ${money(summary.repair_parts_profit)}`} testid="stat-value" /> : <div />}
        </div>
      )}

      {summary?.low_stock > 0 && !low && (
        <button onClick={() => setLow(true)} className="mb-4 w-full flex items-center gap-2 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm font-semibold text-amber-800 text-left" data-testid="low-stock-warning">
          <AlertTriangle className="h-4 w-4" /> {summary.low_stock} part{summary.low_stock > 1 ? "s are" : " is"} at or below minimum stock — tap to view
        </button>
      )}

      <div className="flex flex-col md:flex-row gap-3 md:items-center">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" placeholder="Search by phone model, part, category, brand, bin or barcode" value={q} onChange={(e) => setQ(e.target.value)} data-testid="parts-search-input" />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="md:w-52" data-testid="parts-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {(summary?.categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm font-semibold whitespace-nowrap"><Switch checked={low} onCheckedChange={setLow} data-testid="parts-low-toggle" /> Low stock only</label>
        <button onClick={() => setScan(true)} className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-ink px-4 py-2 text-sm font-bold hover:bg-ink hover:text-white transition-colors" data-testid="parts-scan-btn"><ScanLine className="h-4 w-4" /> Scan</button>
        {owner && <button onClick={() => setEditing({})} className="inline-flex items-center justify-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-700" data-testid="parts-add-btn"><Plus className="h-4 w-4" /> Add part</button>}
      </div>

      <div className="mt-4 grid gap-3">
        {parts === null && <p className="text-sm text-slate-500">Loading…</p>}
        {parts?.length === 0 && <p className="text-sm text-slate-500" data-testid="parts-empty">No parts found.</p>}
        {parts?.map((p) => (
          <PartRow key={p.id} part={p} owner={owner} highlight={highlight === p.id} onStock={(part, type) => setStock({ part, type })} onEdit={setEditing} onDelete={remove} onHistory={setHistory} />
        ))}
      </div>

      {owner && <PartForm part={editing} onClose={() => setEditing(null)} onSave={save} />}
      <StockDialog stock={stock} owner={owner} onClose={() => setStock(null)} onDone={() => { setStock(null); load(); }} />
      <ScannerDialog open={scan} onOpenChange={setScan} onCode={onScanned} />
      <Dialog open={!!history} onOpenChange={(o) => !o && setHistory(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="part-history-dialog">
          <DialogHeader><DialogTitle>Stock history · {history?.name}</DialogTitle></DialogHeader>
          {history && <MovementHistory owner={owner} partId={history.id} compact />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Stat = ({ label, value, sub, warn, testid, onClick }) => (
  <button type="button" onClick={onClick} disabled={!onClick} className={`text-left rounded-xl border p-4 bg-white ${warn ? "border-amber-300" : "border-slate-200"} ${onClick ? "hover:border-brand" : "cursor-default"}`} data-testid={testid}>
    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
    <p className={`font-display mt-1 text-2xl font-black ${warn ? "text-amber-700" : "text-ink"}`}>{value}</p>
    {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
  </button>
);
